import React, { type FC } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
  Pagination,
  SkeletonLoader
} from '@awell-health/design-system'
import type { Activity } from 'app-care/src/hooks/useActivityFeed'
import { ActivityRow } from './ActivityRow'
import { t } from 'i18next'
import type { SortingState } from 'app-care/src/hooks/usePagination'

interface ActivitiesTableProps {
  activities: Activity[]
  getActivityPathwayTitle: (activity: Activity) => string
  emptyMessage?: string
  loading?: boolean
  totalCount?: number
  currentPage?: number
  perPage?: number
  sorting?: SortingState
  onPageChange?: (page: number) => void
  onSortChange?: (field: string, direction?: 'asc' | 'desc') => void
}

interface ActivityTableColumn {
  title: string
  field: string
  sorting: boolean
}

export const ActivitiesTable: FC<ActivitiesTableProps> = ({
  activities,
  getActivityPathwayTitle,
  emptyMessage = t('workinglist:no_activities_found'),
  loading = false,
  totalCount = 0,
  currentPage = 1,
  perPage = 10,
  sorting,
  onPageChange,
  onSortChange
}) => {
  const columns: ActivityTableColumn[] = [
    {
      title: t('workinglist:column_pathway_name'),
      field: 'pathway',
      sorting: false
    },
    {
      title: t('workinglist:column_step_title'),
      field: 'container_name',
      sorting: false
    },
    {
      title: t('workinglist:column_activity_title'),
      field: 'object',
      sorting: false
    },
    {
      title: t('workinglist:column_activity_date'),
      field: 'date',
      sorting: true
    }
  ]

  const totalPages = Math.ceil(totalCount / perPage)

  if (loading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ title, field }) => (
              <TableHead key={field}>{title}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(10)].map((_, i) => (
            <TableRow key={i}>
              {columns.map((_, j) => (
                <TableCell key={j} className="h-14">
                  <SkeletonLoader className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableCaption className="py-0">
          <Pagination
            totalCount={totalCount}
            onPageChange={onPageChange || (() => {})}
            perPage={perPage}
            page={currentPage}
          />
        </TableCaption>
      </Table>
    )
  }

  if (activities.length === 0) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ title, field }) => (
              <TableHead key={field}>{title}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500">
              {emptyMessage}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(({ title, field, sorting: isSortable }) => (
            <TableHead
              className={field === 'date' ? 'w-40' : ''}
              key={field}
              onClick={() => {
                if (isSortable && onSortChange) {
                  onSortChange(field)
                }
              }}
              order={sorting?.field === field ? sorting.direction : undefined}
              sortable={isSortable}
            >
              {title}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {activities.map(activity => (
          <ActivityRow key={activity.id} activity={activity} getActivityPathwayTitle={getActivityPathwayTitle} />
        ))}
      </TableBody>
      {totalPages > 1 && (
        <TableCaption className="py-0">
          <Pagination
            totalCount={totalCount}
            onPageChange={onPageChange || (() => {})}
            perPage={perPage}
            page={currentPage}
          />
        </TableCaption>
      )}
    </Table>
  )
}
